import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {createRef, ref} from 'lit/directives/ref.js';
import store from '../../../store';

import {pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';
import {
	selectArticleCode,
	selectCRI, selectDiameter, selectIsOnesided,
	selectIsSingle,
	selectSystemLumen,
	selectSystemLumenPerPower,
	selectSystemLumenString,
	selectSystemPower,
	selectSystemPowerString, selectWeight, selectWindsurfacearea, selectVariantCodeTypes
} from '../../../selectors';

@customElement('line-tender-text')
export class LineTenderText extends connect(store)(LitElement) {

	static styles = css`
	  p {
		margin: 0 0 10px 0;
		font: var(--font-specsheet-light);
		line-height: 140%;
	  }
	  .info {
		font: var(--font-specsheet-small-light);
	  }
	  @media print {
		p {
		  margin: 0 0 1.5mm 0;
		  margin-bottom: 0;
		  font: var(--font-specsheet-print-light);
		  line-height: 120%;
		}
	  }

	`;

	tenderTextRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.decisions = state.decisions.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}

	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;

		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');

		navigator.clipboard.writeText(tenderText).then(
			() => {
			},
			(err) => {
				console.error('Async: Could not copy text: ', err);
			});
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.decisions, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.decisions, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.decisions, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		return html`
			<div ${ref(this.tenderTextRef)}>
				<p>
					Line
					${when(!selectIsSingle(s),
							() => html`
								${filterAndJoin([
									this.t('HS_HousingType', 'FV'),
									this.t('HS_HousingShape', 'FV'),
									this.t('HS_HousingType', 'FV.L'),
									this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
									this.t('LU_LuminaireDescription', 'D.L')
								], ', ')}${this.t('HS_Construction', 'F', null, ', ', ':')}
								${filterAndJoin([
									this.t('HS_Construction', 'FV.S'),
									this.t('LE_ColourTemperature_1', 'F') +
									this.t('LE_ColourTemperature_1', 'FV.S', null, ' ') +
									this.t('LE_ColourTemperature_2', 'FV.S', null, ' + ')
								], ', ')}
							`,
							() => html`
								${filterAndJoin([
									this.t('HS_HousingType', 'FV'),
									this.t('HS_HousingShape', 'FV'),
									this.t('HS_HousingType', 'FV.L'),
									this.t('LU_LuminaireDescription', 'D.L'),
									this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
									this.t('LE_ColourTemperature_1', 'FV.S')
								], ', ')}
							`)}
				</p>
				<p>
					${filterAndJoin([
						this.t('LE_ColourTemperature_1', 'F', null, '', ' LED ') +
						this.t('LE_ColourTemperature_1', 'FV') + this.t('LE_ColourTemperature_2', 'FV', null, ' + '),

						this.t('LU_SystemLuminousFlux', 'D', null, '', ' ') +
						(selectSystemLumen(s) +
								this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S', null, ' ', ' ') +
								(!selectIsOnesided(s) ? ' (' + selectSystemLumenString(s) + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S', null, ' ') + ')' : '')
						),

						this.t('LU_SystemWattage', 'D', null, '', ' ') +
						(selectSystemPower(s) +
								this.t('LU_SystemWattageUnit.W', 'DV.S', null, ' ', ' ') +
								(!selectIsOnesided(s) ? ' (' + selectSystemPowerString(s) + this.t('LU_SystemWattageUnit.W', 'DV.S', null, ' ') + ')' : '')
						),

						this.t('LU_Efficacy', 'D', null, '', ' ') +
						(selectSystemLumenPerPower(s) + this.t('LU_EfficacyUnit', 'D', null, ' ', '')),

						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV'),
					], ', ')}
				</p>
				<p>
					${this.t('LE_LightEngineDescription', 'D', null, '', ':')}
					${filterAndJoin([
						this.t('LE_LightEngineModuleSize_1', 'FV.L') + this.t('LE_LightEngineModuleSize_2', 'FV.L', null, ' + '),
						this.t('HS_LightEmission', 'FV.L', null, '')
					], ', ')}
				</p>
				<p>
					${this.t('HS_ModuleCutoutSize_1', 'F.S', null, '', ':')}
					${filterAndJoin([
						this.t('LE_LightEngineModuleSize_1', 'FV.S'),
						this.t('LE_LightDistribution_1', 'FV.S'),
						this.t('LE_LightDistribution_1', 'FV.L'),
						this.t('LE_OpticalVersion_1', 'FV.S'),
						this.t('LE_ReflectorColour_1', 'FV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
					], ', ')}
				</p>
				${when(!selectIsOnesided(s), () => html`
					<p>
						${this.t('HS_ModuleCutoutSize_2', 'F.S', null, '', ':')}
						${filterAndJoin([
							this.t('LE_LightEngineModuleSize_2', 'FV.S'),
							this.t('LE_LightDistribution_2', 'FV.S'),
							this.t('LE_LightDistribution_2', 'FV.L'),
							this.t('LE_OpticalVersion_2', 'FV.S'),
							this.t('LE_ReflectorColour_2', 'FV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
						], ', ')}
					</p>
				`)}
				<p>
					${this.t('EB_Zhaga_1', 'F', null, '', ':')}
					${this.t('EB_Zhaga_1', 'FV.L')}
				</p>
				<p>
					${this.t('EB_Control_1', 'F', null, '', ':')}
					${this.t('EB_Control_1', 'FV.S')}
					${this.t('EB_Control_1', 'FV.L')}
				</p>
				<p>
					${this.t('EB_ConstantLumenTechnology_1', 'F', null, '', ':')}
					${this.t('EB_ConstantLumenTechnology_1', 'FV.S')}
				</p>
				<p>
					${this.t('LU_SurgeProtection', 'D')}
					${this.t('LU_SurgeProtection.10 kV', 'DV.S', null, '', ',')}
					${this.t('LU_TechnicalRegion', 'D')}
					${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ',')}
					${this.t('LU_AmbientTemperature', 'D')}
					${this.t('LU_AmbientTemperature.-20_+25', 'DV.S')}
					${this.t('LU_AmbientTemperatureUnit.GradCelsius', 'DV.S')}
				</p>
				<p>
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
						this.t('HS_Base', 'FV.L', null, ' ', ''),
						this.t('HS_NumberPoleDoors', 'FV.S'),
						this.t('HS_HousingMountingLocationEblock_1', 'F', null, '', ': ') + this.t('EB_EBlockMountingType_1', 'FV.S'),
						!selectIsOnesided(s) ? this.t('HS_HousingMountingLocationEblock_2', 'F', null, '', ': ') + this.t('EB_EBlockMountingType_2', 'FV.S') : '',
						this.t('HS_HousingPowerConnection', 'FV.S') + this.t('HS_HousingPowerConnection', 'FV.L', null, ' '),
						this.t(this.c('DescriptiveAttributeReference.LU_FuseBox'), 'DV.L'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingColour', 'F', null, '', ': ') +
						this.t('HS_HousingColour', 'FV'),
						this.t('HS_HousingCoating', 'F', null, '', ': ') +
						this.t('HS_HousingCoating', 'FV'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingHeight', 'F') +
						this.t('HS_HousingHeight', 'FV', null, ' ',),

						this.t('HS_HousingDiameter', 'D', null, ' ') +
						this.t('HS_HousingDiameter.114', 'DV.S', null, ' ') +
						this.t('HS_HousingDiameterUnit.MM', 'DV.S', null, ' '),

						this.t('LU_LuminaireWeight', 'D', null, ' ', ' ') +
						selectWeight(s) +
						this.t('LU_LuminaireWeightUnit', 'D', null, ' '),

						this.t('HS_WindSurfaceArea', 'D', null, '', ' ') +
						selectWindsurfacearea(s) +
						this.t('HS_WindSurfaceAreaUnit.M2', 'DV.S', null, ' ')
					], ', ')}
				</p>
				<p>
					<!-- join -->
					${filterAndJoin([
						this.t('LU_IP.IP65', 'DV'),
						this.t('LU_IK.08', 'DV'),
						this.t('EB_ElectricalProtectionClass_1', 'FV.L'),
						this.t('LU_ENEC_CertificateDescription.ENEC', 'DV'),
						this.t('LU_CE', 'D'),
					], ', ')}
				</p>
				<p>
					<br>
					${this.t('TE_Manufacturer', 'D', null, '', ':')}
					Selux
				</p>
				<!--
				<p>
					Typ: ${selectArticleCode(s)}<br>
				</p>
				-->
				${when(this.state.status.features.variantCodes,
						() => html`
							<p class="info">
								${repeat(
										selectVariantCodeTypes(s),
										(variantCode) => html`${variantCode.id}: ${variantCode.code}<br>`
								)}
							</p>`
				)}
				<p>
					<trans-label .key="${this.c('DescriptiveAttributeReference.LU_Accessories')}" context="DV.L"></trans-label>
				</p>
			</div>
		`;
	}
}
